import { useState, useMemo, useEffect } from 'react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Checkbox } from '@/components/ui/checkbox';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@/components/ui/collapsible';
import {
  Loader2,
  CheckCircle2,
  AlertCircle,
  UserPlus,
  Users,
  RefreshCw,
  Check,
  Award,
  Building2,
  MapPin,
  Plus,
  X,
  ArrowLeft,
  ChevronDown
} from 'lucide-react';
import { cn } from '@/lib/utils';

/**
 * Get status badge styles matching the qualifications tab
 */
function getStatusBadgeStyles(status, expiryDate) {
  const now = new Date();
  const expiry = expiryDate ? new Date(expiryDate) : null;
  const isExpiringSoon = expiry && expiry > now && expiry <= new Date(Date.now() + 30 * 24 * 60 * 60 * 1000);
  const isExpired = expiry && expiry < now;

  if (status === 'completed' && !isExpired && !isExpiringSoon) {
    return { bg: 'bg-green-50', text: 'text-green-700', border: 'border-green-200', label: 'Valid' };
  } else if (status === 'completed' && isExpiringSoon) {
    return { bg: 'bg-amber-50', text: 'text-amber-700', border: 'border-amber-300', label: 'Expiring' };
  } else if (status === 'completed' && isExpired) {
    return { bg: 'bg-red-50', text: 'text-red-700', border: 'border-red-200', label: 'Expired' };
  } else if (status === 'pending') {
    return { bg: 'bg-gray-50', text: 'text-gray-600', border: 'border-gray-200', label: 'Pending' };
  }
  return { bg: 'bg-gray-50', text: 'text-gray-500', border: 'border-gray-200', label: 'Missing' };
}

/**
 * Accordion-style person row for recommendations
 * Expands to show full qualifications list
 */
function PersonRow({
  person,
  isSelected = false,
  onToggle,
  isAssigned,
  onAssign,
  onUnassign,
  variant = 'recommended',
  qualifications = [],
  currentRequirementId = null
}) {
  const [isHovered, setIsHovered] = useState(false);
  const [isExpanded, setIsExpanded] = useState(false);

  useEffect(() => {
    setIsHovered(false);
  }, [isAssigned]);

  const initials = person.name
    ?.split(' ')
    .map(n => n[0])
    .join('')
    .toUpperCase()
    .slice(0, 2) || '??';

  // Sort qualifications: current requirement first, then by status
  const sortedQuals = useMemo(() => {
    if (!qualifications || qualifications.length === 0) return [];
    return [...qualifications].sort((a, b) => {
      if (a.requirementId === currentRequirementId) return -1;
      if (b.requirementId === currentRequirementId) return 1;
      const priority = { completed: 0, pending: 1 };
      return (priority[a.status] || 2) - (priority[b.status] || 2);
    });
  }, [qualifications, currentRequirementId]);

  const hasQualifications = sortedQuals.length > 0;

  return (
    <Collapsible open={isExpanded} onOpenChange={setIsExpanded}>
      <div className={cn(
        "rounded-lg border transition-colors",
        isSelected ? "bg-blue-50 border-blue-200" : "bg-white border-gray-100 hover:border-gray-200",
        isExpanded && "border-gray-200"
      )}>
        {/* Main row - always visible */}
        <div className={cn(
          "flex items-center gap-3 p-3",
          hasQualifications && "cursor-pointer"
        )}>
          <Checkbox
            checked={isSelected}
            onCheckedChange={onToggle}
            onClick={(e) => e.stopPropagation()}
            className="data-[state=checked]:bg-blue-600 flex-shrink-0"
          />

          <Avatar className="h-9 w-9 flex-shrink-0">
            <AvatarImage src={person.avatar} />
            <AvatarFallback className="text-xs font-medium bg-gray-100 text-gray-700">
              {initials}
            </AvatarFallback>
          </Avatar>

          <CollapsibleTrigger asChild disabled={!hasQualifications}>
            <div className={cn("flex-1 min-w-0 flex items-center gap-2", hasQualifications && "cursor-pointer")}>
              {/* Name + role/department */}
              <div className="flex items-center gap-2 min-w-0 flex-1">
                <span className="text-sm font-medium text-gray-900 truncate">
                  {person.name}
                </span>
                {variant === 'recommended' && (
                  <CheckCircle2 className="h-3.5 w-3.5 text-green-600 flex-shrink-0" />
                )}
                {(person.jobTitle || person.department) && (
                  <span className="text-xs text-gray-400 flex-shrink-0">•</span>
                )}
                <span className="text-xs text-gray-500 truncate">
                  {[person.jobTitle, person.department].filter(Boolean).join(' · ')}
                </span>
              </div>

              {/* Quals count + chevron */}
              {hasQualifications && (
                <div className="flex items-center gap-1.5 flex-shrink-0">
                  <Badge variant="secondary" className="text-[10px] px-1.5 py-0 h-5 gap-1">
                    <Award className="h-3 w-3" />
                    {sortedQuals.length}
                  </Badge>
                  <ChevronDown className={cn(
                    "h-3.5 w-3.5 text-gray-400 transition-transform duration-200",
                    isExpanded && "rotate-180"
                  )} />
                </div>
              )}
            </div>
          </CollapsibleTrigger>

          {isAssigned ? (
            <Button
              variant="outline"
              size="sm"
              onMouseEnter={() => setIsHovered(true)}
              onMouseLeave={() => setIsHovered(false)}
              onClick={(e) => {
                e.stopPropagation();
                onUnassign?.(person);
              }}
              className={cn(
                "h-8 w-[100px] flex-shrink-0 transition-all",
                isHovered
                  ? "bg-red-50 text-red-600 border-red-200 hover:bg-red-100"
                  : "bg-green-50 text-green-700 border-green-200 hover:bg-green-100"
              )}
            >
              {isHovered ? (
                <>
                  <X className="h-3.5 w-3.5 mr-1.5" />
                  Unassign
                </>
              ) : (
                <>
                  <Check className="h-3.5 w-3.5 mr-1.5" />
                  Assigned
                </>
              )}
            </Button>
          ) : (
            <Button
              variant="outline"
              size="sm"
              onClick={(e) => {
                e.stopPropagation();
                onAssign(person);
              }}
              className="h-8 w-[100px] flex-shrink-0"
            >
              <Plus className="h-3.5 w-3.5 mr-1.5" />
              Assign
            </Button>
          )}
        </div>

        {/* Expanded content - qualifications list */}
        <CollapsibleContent>
          <div className="px-3 pb-3">
            <div className="border-t border-gray-100 pt-3">
              <div className="text-xs font-medium text-gray-500 mb-2 px-1">Current Qualifications</div>
              <div className="space-y-1 max-h-[200px] overflow-y-auto">
                {sortedQuals.map((qual, idx) => {
                  const styles = getStatusBadgeStyles(qual.status, qual.expiryDate);
                  const isCurrent = qual.requirementId === currentRequirementId;

                  return (
                    <div
                      key={qual.requirementId || idx}
                      className={cn(
                        "flex items-center gap-2 px-2 py-2 rounded-md text-xs",
                        isCurrent ? "bg-blue-50 border border-blue-200" : "bg-gray-50"
                      )}
                    >
                      <div className={cn(
                        "w-2 h-2 rounded-full flex-shrink-0",
                        styles.text.replace('text-', 'bg-').replace('-700', '-500').replace('-600', '-400')
                      )} />
                      <div className="flex-1 min-w-0 flex flex-col">
                        <span className={cn(
                          "truncate",
                          isCurrent ? "text-blue-900 font-medium" : "text-gray-700"
                        )}>
                          {qual.requirementName || 'Unknown'}
                        </span>
                        {qual.projectName && (
                          <span className="text-[10px] text-gray-400 truncate flex items-center gap-1">
                            <MapPin className="h-2.5 w-2.5 inline" />
                            {qual.projectName}
                          </span>
                        )}
                      </div>
                      <Badge
                        variant="outline"
                        className={cn(
                          "text-[9px] px-1.5 py-0 h-4 flex-shrink-0",
                          styles.bg, styles.text, styles.border
                        )}
                      >
                        {styles.label}
                      </Badge>
                      {isCurrent && (
                        <Badge variant="outline" className="text-[9px] px-1.5 py-0 h-4 bg-blue-50 text-blue-700 border-blue-200 flex-shrink-0">
                          Current
                        </Badge>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        </CollapsibleContent>
      </div>
    </Collapsible>
  );
}

/**
 * Compact stats bar - clean professional layout
 */
function StatsSummary({ spaceTrained, companyWide, total }) {
  const stats = [
    {
      label: 'Space Trained',
      value: spaceTrained,
      icon: Award,
      color: 'text-gray-700'
    },
    {
      label: 'Company Trained',
      value: companyWide,
      icon: Building2,
      color: 'text-gray-700'
    },
    {
      label: 'Total Members',
      value: total,
      icon: Users,
      color: 'text-gray-700'
    }
  ];

  return (
    <div className="flex items-center justify-center gap-6">
      {stats.map((stat, idx) => (
        <div key={idx} className="flex items-center gap-2">
          <stat.icon className="h-4 w-4 text-gray-400" />
          <span className="text-sm text-gray-600">{stat.label}</span>
          <span className={cn("text-sm font-semibold", stat.color)}>{stat.value}</span>
        </div>
      ))}
    </div>
  );
}

/**
 * Inline Recommendations Panel - slides in from right
 * Not a dialog/sheet - just a panel component for the sliding layout
 */
export function RecommendationsPanel({
  requirement,
  recommendations,
  allMembers = [],
  currentAssignments = [],
  companyWideTrainedMembers = [],
  companyWideAllMembers = [],
  memberQualifications = {},
  spaceRequirements = [],
  isLoading = false,
  error = null,
  onAssign,
  onUnassign,
  onAssignMultiple,
  onAssignCompanyWide,
  onRetry,
  onClose
}) {
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [activeTab, setActiveTab] = useState('space');
  const [confirmDialog, setConfirmDialog] = useState({ open: false, person: null });

  const data = useMemo(() => {
    if (!recommendations) return null;
    const rec = Array.isArray(recommendations) ? recommendations[0] : recommendations;
    return rec;
  }, [recommendations]);

  // Space members - split into trained and untrained
  const spaceTrainedMembers = data?.alreadyTrained || [];
  const spaceTrainedIds = new Set(spaceTrainedMembers.map(m => m.employeeId));
  const spaceUntrainedMembers = allMembers.filter(m => !spaceTrainedIds.has(m.id) && !spaceTrainedIds.has(m.user_id));
  const spaceTotal = allMembers.length;

  // Company-wide members (not in space) - split into trained and untrained
  const companyTrainedIds = new Set(companyWideTrainedMembers.map(m => m.employeeId));
  const companyUntrainedMembers = companyWideAllMembers.filter(m => !companyTrainedIds.has(m.employeeId));
  // Total = unique members (trained might include people not in company_members table)
  const allCompanyWideIds = new Set([
    ...companyWideTrainedMembers.map(m => m.employeeId),
    ...companyWideAllMembers.map(m => m.employeeId)
  ]);
  const companyTotal = allCompanyWideIds.size;

  const isAssigned = (employeeId) => {
    const memberById = allMembers.find(m => m.id === employeeId);
    if (memberById) {
      return currentAssignments.some(a => a.team_member_id === memberById.user_id);
    }
    const memberByUserId = allMembers.find(m => m.user_id === employeeId);
    if (memberByUserId) {
      return currentAssignments.some(a => a.team_member_id === memberByUserId.user_id);
    }
    return currentAssignments.some(a => a.team_member_id === employeeId);
  };

  const toggleSelection = (employeeId) => {
    setSelectedIds(prev => {
      const next = new Set(prev);
      if (next.has(employeeId)) {
        next.delete(employeeId);
      } else {
        next.add(employeeId);
      }
      return next;
    });
  };

  const handleAssignSelected = () => {
    if (onAssignMultiple && selectedIds.size > 0) {
      onAssignMultiple(Array.from(selectedIds));
      setSelectedIds(new Set());
    }
  };

  const handleUnassignSelected = () => {
    if (selectedIds.size > 0) {
      Array.from(selectedIds).forEach(id => {
        // Find the person object to get the name
        const person = spaceTrainedMembers.find(m => m.employeeId === id) ||
          spaceUntrainedMembers.find(m => m.id === id || m.user_id === id) ||
          companyWideTrainedMembers.find(m => m.employeeId === id) ||
          companyUntrainedMembers.find(m => m.employeeId === id);
        if (person && onUnassign) {
          onUnassign(person.employeeId || person.id, person.name);
        }
      });
      setSelectedIds(new Set());
    }
  };

  // Count how many selected are assigned vs unassigned
  const selectedAssignedCount = Array.from(selectedIds).filter(id => isAssigned(id)).length;
  const selectedUnassignedCount = selectedIds.size - selectedAssignedCount;

  const handleAssign = (person, isCompanyWide = false) => {
    if (isCompanyWide) {
      setConfirmDialog({ open: true, person });
    } else if (onAssign) {
      onAssign(person.employeeId, person.name);
    }
  };

  const handleConfirmCompanyWideAssign = () => {
    const person = confirmDialog.person;
    if (person && onAssignCompanyWide) {
      onAssignCompanyWide(person.employeeId, person.name, person);
    }
    setConfirmDialog({ open: false, person: null });
  };

  const handleUnassign = (person) => {
    if (onUnassign) {
      onUnassign(person.employeeId, person.name);
    }
  };

  return (
    <>
      <div className="flex flex-col h-full bg-white">
        {/* Header */}
        <div className="px-6 py-4 border-b bg-white flex items-center gap-3">
          <Button
            variant="ghost"
            size="icon"
            onClick={onClose}
            className="h-9 w-9 -ml-2"
          >
            <ArrowLeft className="h-4 w-4" />
          </Button>
          <div className="flex-1 min-w-0">
            <h2 className="text-lg font-semibold text-gray-900">
              Assignment Recommendations
            </h2>
            {requirement && (
              <p className="text-sm text-gray-600 truncate mt-0.5">
                {requirement.name}
              </p>
            )}
          </div>
          {!isLoading && !error && data && (
            <Button variant="ghost" size="icon" onClick={onRetry} className="h-9 w-9">
              <RefreshCw className="h-4 w-4" />
            </Button>
          )}
        </div>

        {/* Content */}
        <div className="flex-1 overflow-hidden flex flex-col">
          {isLoading ? (
            <div className="flex-1 flex flex-col items-center justify-center py-12 gap-3">
              <Loader2 className="h-8 w-8 animate-spin text-blue-600" />
              <p className="text-sm text-gray-600">Loading team data...</p>
            </div>
          ) : error ? (
            <div className="flex-1 flex flex-col items-center justify-center py-12 gap-4">
              <div className="h-12 w-12 rounded-full bg-red-50 flex items-center justify-center">
                <AlertCircle className="h-6 w-6 text-red-600" />
              </div>
              <div className="text-center">
                <p className="text-sm font-medium text-gray-900">Failed to load recommendations</p>
                <p className="text-sm text-gray-500 mt-1">{error.message || 'Something went wrong.'}</p>
              </div>
              <Button variant="outline" size="sm" onClick={onRetry}>
                <RefreshCw className="h-4 w-4 mr-2" />
                Try Again
              </Button>
            </div>
          ) : data ? (
            <>
              {/* Stats */}
              <div className="px-6 py-5 border-b bg-white">
                <StatsSummary
                  spaceTrained={spaceTrainedMembers.length}
                  companyWide={companyWideTrainedMembers.length}
                  total={spaceTotal + companyTotal}
                />
              </div>

              {/* Tabs */}
              <Tabs value={activeTab} onValueChange={setActiveTab} className="flex-1 flex flex-col overflow-hidden">
                <TabsList className="w-full justify-start rounded-none border-b bg-white px-6 h-12 gap-0">
                  <TabsTrigger
                    value="space"
                    className="relative rounded-none border-b-2 border-transparent px-4 pb-3 pt-2 data-[state=active]:border-b-gray-900 data-[state=active]:bg-transparent data-[state=active]:shadow-none data-[state=active]:text-gray-900 text-gray-500 hover:text-gray-700"
                  >
                    <MapPin className="h-4 w-4 mr-2" />
                    This Space
                    {spaceTotal > 0 && (
                      <Badge variant="secondary" className="ml-2 h-5 px-1.5 text-xs">
                        {spaceTotal}
                      </Badge>
                    )}
                  </TabsTrigger>
                  <TabsTrigger
                    value="company"
                    className="relative rounded-none border-b-2 border-transparent px-4 pb-3 pt-2 data-[state=active]:border-b-gray-900 data-[state=active]:bg-transparent data-[state=active]:shadow-none data-[state=active]:text-gray-900 text-gray-500 hover:text-gray-700"
                  >
                    <Building2 className="h-4 w-4 mr-2" />
                    Company Wide
                    <Badge variant="secondary" className="ml-2 h-5 px-1.5 text-xs">
                      {companyTotal}
                    </Badge>
                  </TabsTrigger>
                </TabsList>

                {/* This Space Tab */}
                <TabsContent value="space" className="m-0 mt-0 flex-1 overflow-hidden data-[state=inactive]:hidden">
                  <ScrollArea className="h-full">
                    <div className="p-6 space-y-6">
                      {/* Already Trained Section */}
                      {spaceTrainedMembers.length > 0 && (
                        <div className="space-y-2">
                          <div className="flex items-center gap-2 mb-3">
                            <Award className="h-4 w-4 text-green-600" />
                            <h3 className="text-sm font-medium text-gray-900">Already Trained</h3>
                            <Badge variant="secondary" className="ml-auto h-5 px-2 text-xs bg-green-50 text-green-700">
                              {spaceTrainedMembers.length}
                            </Badge>
                          </div>
                          <div className="space-y-2">
                            {spaceTrainedMembers.map((person, idx) => (
                              <PersonRow
                                key={person.employeeId || idx}
                                person={person}
                                variant="recommended"
                                isSelected={selectedIds.has(person.employeeId)}
                                onToggle={() => toggleSelection(person.employeeId)}
                                isAssigned={isAssigned(person.employeeId)}
                                onAssign={handleAssign}
                                onUnassign={handleUnassign}
                                qualifications={memberQualifications[person.userId] || memberQualifications[person.employeeId] || []}
                                currentRequirementId={requirement?.id}
                              />
                            ))}
                          </div>
                        </div>
                      )}

                      {/* Other Space Members Section */}
                      {spaceUntrainedMembers.length > 0 && (
                        <div className="space-y-2">
                          <div className="flex items-center gap-2 mb-3">
                            <Users className="h-4 w-4 text-gray-400" />
                            <h3 className="text-sm font-medium text-gray-900">Other Space Members</h3>
                            <Badge variant="secondary" className="ml-auto h-5 px-2 text-xs">
                              {spaceUntrainedMembers.length}
                            </Badge>
                          </div>
                          <div className="space-y-2">
                            {spaceUntrainedMembers.map((member) => (
                              <PersonRow
                                key={member.id}
                                person={{
                                  employeeId: member.id,
                                  name: member.name,
                                  jobTitle: member.role,
                                  avatar: member.avatar
                                }}
                                variant="all"
                                isSelected={selectedIds.has(member.id)}
                                onToggle={() => toggleSelection(member.id)}
                                isAssigned={isAssigned(member.id)}
                                onAssign={handleAssign}
                                onUnassign={handleUnassign}
                                qualifications={memberQualifications[member.user_id] || memberQualifications[member.id] || []}
                                currentRequirementId={requirement?.id}
                              />
                            ))}
                          </div>
                        </div>
                      )}

                      {/* Empty State */}
                      {spaceTrainedMembers.length === 0 && spaceUntrainedMembers.length === 0 && (
                        <div className="text-center py-12 text-gray-500">
                          <Users className="h-12 w-12 text-gray-300 mx-auto mb-3" />
                          <p className="text-sm font-medium text-gray-900">No space members</p>
                          <p className="text-sm text-gray-500 mt-1">Add team members to this space first.</p>
                        </div>
                      )}
                    </div>
                  </ScrollArea>
                </TabsContent>

                {/* Company Wide Tab */}
                <TabsContent value="company" className="m-0 mt-0 flex-1 overflow-hidden data-[state=inactive]:hidden">
                  <ScrollArea className="h-full">
                    <div className="p-6 space-y-6">
                      {/* Already Trained Section */}
                      {companyWideTrainedMembers.length > 0 && (
                        <div className="space-y-2">
                          <div className="flex items-center gap-2 mb-3">
                            <Award className="h-4 w-4 text-green-600" />
                            <h3 className="text-sm font-medium text-gray-900">Already Trained</h3>
                            <Badge variant="secondary" className="ml-auto h-5 px-2 text-xs bg-green-50 text-green-700">
                              {companyWideTrainedMembers.length}
                            </Badge>
                          </div>
                          <div className="space-y-2">
                            {companyWideTrainedMembers.map((person, idx) => (
                              <PersonRow
                                key={person.employeeId || idx}
                                person={person}
                                variant="recommended"
                                isSelected={selectedIds.has(person.employeeId)}
                                onToggle={() => toggleSelection(person.employeeId)}
                                isAssigned={isAssigned(person.employeeId)}
                                onAssign={(p) => handleAssign(p, true)}
                                onUnassign={handleUnassign}
                                qualifications={memberQualifications[person.userId] || memberQualifications[person.employeeId] || []}
                                currentRequirementId={requirement?.id}
                              />
                            ))}
                          </div>
                        </div>
                      )}

                      {/* Other Company Members Section */}
                      {companyUntrainedMembers.length > 0 && (
                        <div className="space-y-2">
                          <div className="flex items-center gap-2 mb-3">
                            <Users className="h-4 w-4 text-gray-400" />
                            <h3 className="text-sm font-medium text-gray-900">Other Company Members</h3>
                            <Badge variant="secondary" className="ml-auto h-5 px-2 text-xs">
                              {companyUntrainedMembers.length}
                            </Badge>
                          </div>
                          <div className="space-y-2">
                            {companyUntrainedMembers.map((person, idx) => (
                              <PersonRow
                                key={person.employeeId || idx}
                                person={person}
                                variant="all"
                                isSelected={selectedIds.has(person.employeeId)}
                                onToggle={() => toggleSelection(person.employeeId)}
                                isAssigned={isAssigned(person.employeeId)}
                                onAssign={(p) => handleAssign(p, true)}
                                onUnassign={handleUnassign}
                                qualifications={memberQualifications[person.userId] || memberQualifications[person.employeeId] || []}
                                currentRequirementId={requirement?.id}
                              />
                            ))}
                          </div>
                        </div>
                      )}

                      {/* Empty State */}
                      {companyWideTrainedMembers.length === 0 && companyUntrainedMembers.length === 0 && (
                        <div className="text-center py-12 text-gray-500">
                          <Building2 className="h-12 w-12 text-gray-300 mx-auto mb-3" />
                          <p className="text-sm font-medium text-gray-900">No other company members</p>
                          <p className="text-sm text-gray-500 mt-1">All company members are already in this space.</p>
                        </div>
                      )}
                    </div>
                  </ScrollArea>
                </TabsContent>
              </Tabs>
            </>
          ) : (
            <div className="flex-1 flex flex-col items-center justify-center py-12">
              <p className="text-sm text-gray-500">No data available</p>
            </div>
          )}
        </div>

        {/* Footer with selection bar */}
        {selectedIds.size > 0 && (
          <div className="px-6 py-4 bg-gray-50 border-t flex items-center justify-between">
            <span className="text-sm font-medium text-gray-900">
              {selectedIds.size} selected
            </span>
            <div className="flex items-center gap-2">
              {selectedAssignedCount > 0 && (
                <Button
                  size="sm"
                  variant="outline"
                  onClick={handleUnassignSelected}
                  className="bg-red-50 text-red-600 border-red-200 hover:bg-red-100"
                >
                  <X className="h-4 w-4 mr-2" />
                  Unassign ({selectedAssignedCount})
                </Button>
              )}
              {selectedUnassignedCount > 0 && (
                <Button size="sm" onClick={handleAssignSelected}>
                  <UserPlus className="h-4 w-4 mr-2" />
                  Assign ({selectedUnassignedCount})
                </Button>
              )}
            </div>
          </div>
        )}
      </div>

      {/* Confirmation dialog for company-wide members */}
      <Dialog
        open={confirmDialog.open}
        onOpenChange={(open) => !open && setConfirmDialog({ open: false, person: null })}
      >
        <DialogContent className="sm:max-w-[500px] rounded-2xl border-slate-200">
          <DialogHeader className="pb-4 border-b border-slate-100">
            <DialogTitle className="text-xl font-bold text-slate-800">Add to Space & Assign</DialogTitle>
            <DialogDescription className="text-slate-500">
              This will add the member to your space and assign the requirement.
            </DialogDescription>
          </DialogHeader>
          <div className="py-4 space-y-3">
            <p className="text-sm text-slate-600">
              <strong className="text-slate-900">{confirmDialog.person?.name}</strong> is not currently a member of this space.
            </p>
            <p className="text-sm text-slate-600">
              They will be added as a team member and assigned the requirement.
            </p>
          </div>
          <DialogFooter className="pt-4 border-t border-slate-100 gap-3">
            <Button variant="outline" onClick={() => setConfirmDialog({ open: false, person: null })}>
              Cancel
            </Button>
            <Button onClick={handleConfirmCompanyWideAssign}>
              Add & Assign
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}

export default RecommendationsPanel;
